import * as React from 'react';

/** scrollTop, in px, past which the header's scroll shadow appears. */
export const HEADER_SCROLL_SHADOW_THRESHOLD_PX = 16;

interface IScrollMetrics {
  scrollTop: number;
}

/**
 * Maps a scroller's position to the header scroll progress — binary: 0 until
 * scrollTop crosses HEADER_SCROLL_SHADOW_THRESHOLD_PX, then 1. The CSS side
 * (`entity-header-scroll-shadow` in styles/app.css) fades the sticky header's
 * drop shadow on the snap.
 */
export const computeHeaderScrollProgress = ({ scrollTop }: IScrollMetrics): number =>
  scrollTop > HEADER_SCROLL_SHADOW_THRESHOLD_PX ? 1 : 0;

/**
 * Drives EntityPaneHeader's scroll shadow from the single pane scroller inside
 * the pane card (see scenes/HomeScene.tsx). Attach the returned ref + onScroll
 * to that scroller: on every scroll it writes `--entity-header-scroll-progress`
 * (0 | 1) onto the scroller element, which cascades down to the sticky
 * header's `entity-header-scroll-shadow` utility. Where the scroller sits after
 * a navigation is `usePaneScrollRestoration`'s call; its assignment fires a
 * scroll event, so the progress follows.
 *
 * @returns `scrollerRef` to attach to the scroll container and `onScroll` to
 * wire as its scroll handler.
 */
export const useHeaderScrollProgress = () => {
  const scrollerRef = React.useRef<HTMLDivElement>(null);

  const onScroll = React.useCallback(() => {
    const scroller = scrollerRef.current;
    if (scroller == null) {
      return;
    }
    const progress = computeHeaderScrollProgress({ scrollTop: scroller.scrollTop });
    scroller.style.setProperty('--entity-header-scroll-progress', String(progress));
  }, []);

  return { scrollerRef, onScroll };
};
